import type { TFunction } from 'i18next';
import type { GetChargingKpisResponseDto } from '~/app/services/charging-kpi/charging-kpi.types';
import type { DropOffCausesCardViewModel } from '~/app/landing/operator/service-quality/ServiceQualityPage.types';

export type ChargingRateCard = {
  id: string;
  title: string;
  metric: string;
  description: string;
  capacityLeft: string;
  capacityRight: string;
  progressPercent: number;
};

export type ChargingKpiView = {
  sessionSuccess: ChargingRateCard;
  weightedUptime: ChargingRateCard;
  dropOff: DropOffCausesCardViewModel;
};

const CAUSE_I18N_KEY: Record<string, string> = {
  'payment-terminal': 'kpi.dropOffCauses.causes.paymentTerminal',
  'connector-locking': 'kpi.dropOffCauses.causes.connectorLocking',
  'session-timeout': 'kpi.dropOffCauses.causes.sessionTimeout',
  other: 'kpi.dropOffCauses.causes.other',
};

function formatRate(rate: number | null): string {
  if (rate == null) return '—';
  return `${rate}%`;
}

function formatDelta(rate: number, target: number): number {
  return Math.round(Math.abs(rate - target) * 10) / 10;
}

function formatKwh(kwh: number): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  }).format(kwh);
}

export function mapChargingKpiView(
  dto: GetChargingKpisResponseDto,
  t: TFunction<'dashboard'>,
): ChargingKpiView {
  const { sessionSuccess, weightedUptime, dropOffCauses } = dto;
  const inProgress =
    sessionSuccess.inProgressCount > 0
      ? ` · ${t('kpi.sessionSuccess.inProgress', {
          count: sessionSuccess.inProgressCount,
        })}`
      : '';

  const sessionDescription =
    sessionSuccess.ratePercent == null
      ? `${t('kpi.sessionSuccess.noData')}${inProgress}`
      : `${
          sessionSuccess.ratePercent >= sessionSuccess.targetPercent
            ? t('kpi.sessionSuccess.descriptionAbove', {
                target: sessionSuccess.targetPercent,
                delta: formatDelta(
                  sessionSuccess.ratePercent,
                  sessionSuccess.targetPercent,
                ),
              })
            : t('kpi.sessionSuccess.descriptionBelow', {
                target: sessionSuccess.targetPercent,
                delta: formatDelta(
                  sessionSuccess.ratePercent,
                  sessionSuccess.targetPercent,
                ),
              })
        }${inProgress}`;

  const failureCount = dropOffCauses.reduce(
    (total, cause) => total + cause.count,
    0,
  );

  return {
    sessionSuccess: {
      id: 'session-success',
      title: t('kpi.sessionSuccess.title'),
      metric: formatRate(sessionSuccess.ratePercent),
      description: sessionDescription,
      capacityLeft: t('kpi.sessionSuccess.capacityLeft'),
      capacityRight: t('kpi.sessionSuccess.capacityRight', {
        current: sessionSuccess.ratePercent ?? '—',
        total: 100,
      }),
      progressPercent: sessionSuccess.ratePercent ?? 0,
    },
    weightedUptime: {
      id: 'weighted-uptime',
      title: t('kpi.weightedUptime.title'),
      metric: formatRate(weightedUptime.ratePercent),
      description:
        weightedUptime.ratePercent == null
          ? t('kpi.weightedUptime.noData')
          : t('kpi.weightedUptime.description', {
              included: weightedUptime.chargersIncluded,
              kwh: formatKwh(weightedUptime.totalWeightKwh),
              excluded: weightedUptime.chargersExcludedZeroUsage,
            }),
      capacityLeft: t('kpi.weightedUptime.capacityLeft'),
      capacityRight: t('kpi.weightedUptime.capacityRight', {
        current: weightedUptime.ratePercent ?? '—',
        total: 100,
      }),
      progressPercent: weightedUptime.ratePercent ?? 0,
    },
    dropOff: {
      title: t('kpi.dropOffCauses.title'),
      metric:
        failureCount === 0
          ? t('kpi.dropOffCauses.noData')
          : t('kpi.dropOffCauses.metric', { count: failureCount }),
      bars: dropOffCauses.map((cause) => ({
        id: cause.id,
        label: t(CAUSE_I18N_KEY[cause.id] ?? cause.id),
        percent: cause.percent,
        percentLabel: `${cause.percent}%`,
      })),
    },
  };
}
